import React, { useRef, useState, useCallback, useEffect } from 'react'
import {
  makeStyles,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  IconButton,
  ConfirmationDialog
} from '@paintscout/ui'
import type { DialogProps, MuiTheme as Theme } from '@paintscout/ui'
import { withDialogStackContext } from '@paintscout/ui-business'
import type { WithDialogStackContext } from '@paintscout/ui-business'
import type { QuoteItemConsumer, QuoteItemSection, UpdateableArea } from '@paintscout/util/builder'
import { isLooselyEqual } from '@paintscout/util'
import cloneDeep from 'lodash/cloneDeep'
import CloseIcon from '@material-ui/icons/Close'
import AreaDetailsForm from './AreaDetailsForm'

const useStyles = makeStyles((_theme: Theme) => ({
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  }
}))

export interface AreaDetailsProps {
  section: QuoteItemSection
  area: UpdateableArea
  short?: boolean
  consumer: QuoteItemConsumer
  onConfirm: (area: UpdateableArea) => any
  onCancel: () => any
}

export interface AreaDetailsState {
  showSections?: {
    [key: string]: boolean
  }
  updatedArea?: UpdateableArea
}

function AreaDetails(
  props: AreaDetailsProps & { loading?: boolean } & WithDialogStackContext & {
      handleCloseRef?: React.MutableRefObject<(() => void) | null>
      hasChangesRef?: React.MutableRefObject<(() => boolean) | null>
    }
) {
  const classes = useStyles()
  // Deep clone the initial area to prevent mutation
  const initialAreaRef = useRef<UpdateableArea>(cloneDeep(props.area))
  const [area, setArea] = useState(props.area)
  const { short, onCancel, onConfirm, loading, dialogStackContext, handleCloseRef, hasChangesRef } = props
  const descriptionRef = useRef(null)

  const checkHasChanges = useCallback((): boolean => {
    // Helper function to normalize HTML strings
    const normalizeHTML = (html: string | undefined | null): string => {
      if (!html) return ''
      const trimmed = String(html).trim()
      // Normalize empty HTML variations
      if (
        trimmed === '' ||
        trimmed === '<p></p>' ||
        trimmed === '<p><br></p>' ||
        trimmed === '<br>' ||
        trimmed === '<p><br/></p>'
      ) {
        return ''
      }
      return trimmed
    }

    // Get current description from editor
    let currentDescription = ''
    try {
      const currentDescriptionValue = descriptionRef.current?.getValue()
      if (currentDescriptionValue && typeof currentDescriptionValue === 'object') {
        // getValue() returns an OverridableValue object: { useCustom, custom, default }
        const descValue = currentDescriptionValue.useCustom
          ? currentDescriptionValue.custom
          : currentDescriptionValue.default
        currentDescription = normalizeHTML(descValue)
      } else if (currentDescriptionValue) {
        currentDescription = normalizeHTML(String(currentDescriptionValue))
      }
      // eslint-disable-next-line unused-imports/no-unused-vars
    } catch (e) {
      // If getValue fails, treat as empty
      currentDescription = ''
    }

    // Get initial description
    const initialDescriptionValue = initialAreaRef.current.description?.useCustom
      ? initialAreaRef.current.description.custom
      : initialAreaRef.current.description?.default
    const initialDescription = normalizeHTML(initialDescriptionValue)

    // Compare description separately
    const descriptionChanged = currentDescription !== initialDescription

    // Compare only user-editable fields (ignore calculated dimensions like sqft, lnft, cuft)
    const normalizeValue = (val: any) => {
      if (val === null || val === undefined) return
      if (typeof val === 'number') {
        // Handle floating point precision issues - round to 2 decimal places for comparison
        return Math.round(val * 100) / 100
      }
      if (typeof val === 'string') {
        const trimmed = val.trim()
        return trimmed || undefined
      }
      return val
    }

    const getEditableFields = (area: UpdateableArea) => {
      const dims = area.dimensions
      return {
        label: normalizeValue(area.label),
        areaType: area.areaType,
        quoteType: area.quoteType,
        internalNote: normalizeValue(area.internalNote),
        clientNote: normalizeValue(area.clientNote),
        // Only compare the input dimensions, not calculated ones
        dimensions: dims
          ? {
              width: normalizeValue(dims.width),
              length: normalizeValue(dims.length),
              height: normalizeValue(dims.height),
              override: dims.override === true
            }
          : {
              width: undefined,
              length: undefined,
              height: undefined,
              override: false
            }
      }
    }

    const currentEditableFields = getEditableFields(area)
    const initialEditableFields = getEditableFields(initialAreaRef.current)

    // Use isLooselyEqual for deep comparison
    const otherFieldsChanged = !isLooselyEqual(currentEditableFields, initialEditableFields)
    return descriptionChanged || otherFieldsChanged
  }, [area])

  const handleClose = useCallback(() => {
    const hasChanges = checkHasChanges()

    if (hasChanges) {
      dialogStackContext.openDialog(ConfirmationDialog, {
        message: 'You have unsaved changes. Are you sure you want to close?',
        onConfirm: () => {
          dialogStackContext.dismissDialog() // Dismiss confirmation dialog
          dialogStackContext.dismissAllDialogs() // Close all dialogs
        },
        onCancel: () => {
          dialogStackContext.dismissDialog()
        }
      })
    } else {
      dialogStackContext.dismissAllDialogs() // Close all dialogs when no changes
    }
  }, [checkHasChanges, dialogStackContext])

  // Expose handleClose and checkHasChanges to parent via refs
  useEffect(() => {
    if (handleCloseRef) {
      handleCloseRef.current = handleClose
    }
    if (hasChangesRef) {
      hasChangesRef.current = checkHasChanges
    }
  }, [handleClose, handleCloseRef, checkHasChanges, hasChangesRef])

  return (
    <>
      <DialogTitle
        classes={{ root: classes.dialogTitle }}
        rightContent={
          <IconButton size="small" onClick={handleClose} disabled={loading}>
            <CloseIcon />
          </IconButton>
        }
      >
        Area Details
      </DialogTitle>
      <DialogContent>
        <form
          id="area-details"
          onSubmit={handleConfirm}
          onKeyDown={(keyEvent) => {
            if ((keyEvent.charCode || keyEvent.keyCode) === 13 && !(keyEvent.target as any).editor) {
              handleConfirm(keyEvent)
            }
          }}
        >
          <AreaDetailsForm
            section={props.section}
            area={area}
            onChange={handleChange}
            short={short}
            descriptionRef={descriptionRef}
          />
        </form>
      </DialogContent>
      <DialogActions
        leftButton={
          <Button variant="textNoPadding" onClick={onCancel}>
            Back
          </Button>
        }
      >
        <Button type="submit" form="area-details" className="tour-area-details-confirm-button">
          Next
        </Button>
      </DialogActions>
    </>
  )

  function handleChange(ev: any, newArea: UpdateableArea) {
    setArea(newArea)
  }

  function handleConfirm(ev: any) {
    ev.preventDefault()

    const description = descriptionRef.current?.getValue()

    onConfirm({
      ...area,
      description
    })
  }
}

function AreaDetailsDialog(
  props: AreaDetailsProps &
    DialogProps &
    WithDialogStackContext & { hasChangesRef?: React.MutableRefObject<(() => boolean) | null> }
) {
  const {
    area,
    onCancel,
    onConfirm,
    short,
    consumer,
    section,
    loading,
    dialogStackContext,
    hasChangesRef: parentHasChangesRef,
    ...baseDialogProps
  } = props
  const handleCloseRef = useRef<(() => void) | null>(null)
  const hasChangesRef = useRef<(() => boolean) | null>(null)

  function handleDialogClose() {
    if (handleCloseRef.current) {
      handleCloseRef.current()
    }
  }

  // Expose hasChangesRef to parent via props
  // Run this effect whenever the component renders to ensure the ref is up to date
  useEffect(() => {
    if (parentHasChangesRef && hasChangesRef.current) {
      parentHasChangesRef.current = hasChangesRef.current
    }
  })

  return (
    <Dialog
      maxWidth="md"
      fullWidth={true}
      {...baseDialogProps}
      classes={{ paper: 'tour-area-details-dialog' }}
      onClose={handleDialogClose}
    >
      <AreaDetails
        section={section}
        area={area}
        short={short}
        consumer={consumer}
        onCancel={onCancel}
        onConfirm={onConfirm}
        loading={loading}
        dialogStackContext={dialogStackContext}
        handleCloseRef={handleCloseRef}
        hasChangesRef={hasChangesRef}
      />
    </Dialog>
  )
}

export default withDialogStackContext(AreaDetailsDialog)
